import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Edit as EditIcon,
  Add as AddIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import Header from './Header';

const DocumentTemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const url = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/templates`);
      setTemplates(response.data.templates);
      setError('');
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDocument = (templateId) => {
    window.open(`${url}/templates/${templateId}`, '_blank');
  };

  const handleUploadNew = () => {
    navigate('/template/upload');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={50} />
          <Typography variant="h6" color="text.secondary">
            Loading templates...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <Header />
      
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                Document Templates
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {templates.length} template{templates.length !== 1 ? 's' : ''} available
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleUploadNew}
              sx={{ px: 3, py: 1 }}
            >
              Upload Template
            </Button>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Empty State */}
          {templates.length === 0 && !error && (
            <Paper sx={{ p: 8, textAlign: 'center' }}>
              <DocumentIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                No Templates Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Get started by uploading your first document template
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleUploadNew}
                sx={{ px: 4, py: 1.5 }}
              >
                Upload Your First Template
              </Button>
            </Paper>
          )}

          {/* Templates Grid */}
          {templates.length > 0 && (
            <Grid container spacing={3}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      }
                    }}
                  >
                    
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2.5 }}>
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            p: 1,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <DocumentIcon />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, wordBreak: 'break-word', flex: 1 }}>
                          {template.name}
                        </Typography>
                      </Stack>

                      <Chip 
                        label={`${template.fieldsCount} field${template.fieldsCount !== 1 ? 's' : ''}`}
                        size="medium"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />

                      {template.fields && template.fields.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                            Fields:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {template.fields.slice(0, 4).map((field) => (
                              <Chip 
                                key={field}
                                label={field}
                                size="small"
                              />
                            ))}
                            {template.fields.length > 4 && (
                              <Chip 
                                label={`+${template.fields.length - 4} more`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </CardContent>

                    <CardActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<OpenIcon />}
                        onClick={() => handleOpenDocument(template.id)}
                      >
                        Open Document
                      </Button>
                      
                      <IconButton 
                        sx={{ 
                          border: 1, 
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

        </Container>
      </Box>
    </>
  );
};

export default DocumentTemplateList;